import axios, { AxiosResponse, AxiosError, AxiosPromise } from "axios";
import axiosRetry from "axios-retry";
import { RetryConfig } from "../retry-config";
import {
  getV1AxiosRequestConfig,
  getV2AxiosRequestConfig,
} from "us.helper/utility";

const errorInterceptor = (error: AxiosError) => {
  switch (error?.response?.status) {
    case 400:
      break;
    case 401:
      break;
    case 404:
      break;
    default:
  }
  return error.response;
};

const responseInterceptor = (response: AxiosResponse) => {
  switch (response.status) {
    case 200:
      break;
    case 204:
      break;
    default:
  }

  return response;
};

const axiosInstance = axios.create({
  baseURL: `https://apim-${window._ENV.REACT_APP_APIM_BASE}-${window._ENV.REACT_APP_COLLECTION_API_ENV}.azure-api.net`,
  headers: {
    Accept: "application/json",
    "Content-Type": "application/json",
  },
  transformResponse: [
    function (data, headers) {
      let response;
      try {
        response = data ? JSON.parse(data) : {};
      } catch (error) {
        throw Error(`${JSON.stringify(error)}`);
      }
      return response;
    },
  ],
});

const axiosInstanceForNonJson = axios.create({
  baseURL: `https://apim-${window._ENV.REACT_APP_APIM_BASE}-${window._ENV.REACT_APP_COLLECTION_API_ENV}.azure-api.net`,
  headers: {
    "Content-Type": "application/json",
    Accept: "application/octet-stream",
  },
  responseType: "arraybuffer",
  transformResponse: [
    function (data) {
      let response;
      try {
        response = data ? data : {};
      } catch (error) {
        throw Error(`${JSON.stringify(error)}`);
      }
      return response;
    },
  ],
});

const axiosInstanceMultipart = axios.create({
  baseURL: `https://apim-${window._ENV.REACT_APP_APIM_BASE}-${window._ENV.REACT_APP_COLLECTION_API_ENV}.azure-api.net`,
  headers: {
    Accept: "application/json",
    "Content-Type": "multipart/form-data",
  },
  transformResponse: [
    function (data) {
      let response;
      try {
        response = data ? data : {};
      } catch (error) {
        throw Error(`${JSON.stringify(error)}`);
      }
      return response;
    },
  ],
});

const axiosInstanceGraphExplorer = axios.create({
  baseURL: `https://graph.microsoft.com`,
  headers: {
    Accept: "application/json",
    "Content-Type": "application/json",
  },
  transformResponse: [
    function (data) {
      let response;
      try {
        response = data ? JSON.parse(data) : {};
      } catch (error) {
        throw Error(`${JSON.stringify(error)}`);
      }
      return response;
    },
  ],
});

const axiosInstanceGraphPhoto = axios.create({
  baseURL: `https://graph.microsoft.com`,
  headers: {
    Accept: "image/jpeg",
    "Content-Type": "image/jpeg",
    "Cache-Control": "max-age=604800",
  },
  responseType: "blob",
  transformResponse: [
    function (data, headers) {
      let image;
      try {
        const blob = new Blob([data], {
          type: headers["content-type"],
        });
        const url = window.URL || window.webkitURL;
        image = url.createObjectURL(blob);
      } catch (error) {
        throw Error(`${JSON.stringify(error)}`);
      }
      return image;
    },
  ],
});

const reportingInstance = axios.create({
  baseURL: `https://apim-${window._ENV.REACT_APP_APIM_BASE}-${window._ENV.REACT_APP_COLLECTION_API_ENV}.azure-api.net`,
  headers: {
    Accept: "application/json",
    "Content-Type": "application/json",
    "Access-Control-Allow-Credentials": true,
  },
});

const axiosInstanceForDirectory = axios.create({
  baseURL: `https://apim-${window._ENV.REACT_APP_APIM_BASE}-${window._ENV.REACT_APP_COLLECTION_API_ENV}.azure-api.net`,
  headers: {
    Accept: "application/json",
    "Content-Type": "application/json",
  },
  transformResponse: [
    function (data, headers) {
      let response;
      try {
        response = data ? JSON.parse(data) : {};
      } catch (error) {
        throw Error(`${JSON.stringify(error)}`);
      }
      return response;
    },
  ],
});

// set retry request configuration
axiosRetry(axiosInstance, RetryConfig);
axiosRetry(axiosInstanceForNonJson, RetryConfig);
axiosRetry(axiosInstanceMultipart, RetryConfig);
axiosRetry(reportingInstance, RetryConfig);
axiosRetry(axiosInstanceForDirectory, RetryConfig);

// Set the v2 auth token for any request
axiosInstance.interceptors.request.use(getV2AxiosRequestConfig);
reportingInstance.interceptors.request.use(getV2AxiosRequestConfig);
axiosInstanceForNonJson.interceptors.request.use(getV2AxiosRequestConfig);
axiosInstanceMultipart.interceptors.request.use(getV2AxiosRequestConfig);

/**
 * for the graph explore we will use version 1 token from the ADV1 helper
 */
axiosInstanceGraphExplorer.interceptors.request.use(getV1AxiosRequestConfig);
axiosInstanceGraphPhoto.interceptors.request.use(getV1AxiosRequestConfig);
axiosInstanceForDirectory.interceptors.request.use(getV1AxiosRequestConfig);

axiosInstance.interceptors.response.use(responseInterceptor, errorInterceptor);
axiosInstanceForNonJson.interceptors.response.use(
  responseInterceptor,
  errorInterceptor
);
axiosInstanceMultipart.interceptors.response.use(
  responseInterceptor,
  errorInterceptor
);
axiosInstanceGraphExplorer.interceptors.response.use(
  responseInterceptor,
  errorInterceptor
);
axiosInstanceGraphPhoto.interceptors.response.use(
  responseInterceptor,
  errorInterceptor
);
axiosInstanceForDirectory.interceptors.response.use(
  responseInterceptor,
  errorInterceptor
);

const AzureHttp = {
  reporting: {
    get: function <D extends {} | Array<{}>>(
      service: string,
      endpoint: string,
      data: D,
      version: string | undefined = undefined
    ): AxiosPromise {
      return reportingInstance.get(`/${service}/${version}/${endpoint}`, {
        params: data,
      });
    },
  },
  post: function <D extends {} | Array<{}>>(
    service: string,
    endpoint: string,
    data: D,
    version: string | undefined = undefined
  ): AxiosPromise {
    version;
    return axiosInstance.post(`/${service}/${version}/${endpoint}`, data);
  },
  put: function <D extends {} | Array<{}>>(
    service: string,
    endpoint: string,
    data: D,
    version: string | undefined = undefined
  ): AxiosPromise {
    return axiosInstance.put(`/${service}/${version}/${endpoint}`, data);
  },
  get: function <D extends {} | Array<{}>>(
    service: string,
    endpoint: string,
    data: D,
    version: string | undefined = undefined
  ): AxiosPromise {
    return axiosInstance.get(`/${service}/${version}/${endpoint}`, {
      params: data,
    });
  },
  getById: function (
    service: string,
    endpoint: string,
    id: number | string,
    version: string | undefined = undefined
  ): AxiosPromise {
    return axiosInstance.get(`/${service}/${version}/${endpoint}/${id}`);
  },
  delete: function <D extends {} | Array<{}>>(
    service: string,
    endpoint: string,
    id: number | string,
    data: D,
    version: string | undefined = undefined
  ): AxiosPromise {
    return axiosInstance.delete(`/${service}/${version}/${endpoint}/${id}`, {
      data: data,
    });
  },
  patch: function <D extends {} | Array<{}>>(
    service: string,
    endpoint: string,
    data: D,
    version: string | undefined = undefined
  ): AxiosPromise {
    return axiosInstance.patch(`/${service}/${version}/${endpoint}`, data);
  },
  getFile: function <D extends {} | Array<{}>>(
    service: string,
    endpoint: string,
    data: D,
    version: string | undefined = undefined
  ): AxiosPromise {
    return axiosInstanceForNonJson.get(`/${service}/${version}/${endpoint}`, {
      params: data,
    });
  },
  uploadFile: function (
    service: string,
    endpoint: string,
    data: any,
    version: string | undefined = undefined
  ): AxiosPromise {
    return axiosInstanceMultipart.post(
      `/${service}/${version}/${endpoint}`,
      data
    );
  },
  putById: function <D extends {} | Array<{}>>(
    service: string,
    endpoint: string,
    id: number | string,
    data: D,
    version: string | undefined = undefined
  ): AxiosPromise {
    return axiosInstance.put(`/${service}/${version}/${endpoint}/${id}`, data);
  },
  downloadFile: function (
    service: string,
    endpoint: string,
    data: any,
    version: string | undefined = undefined
  ): AxiosPromise {
    return axiosInstanceForNonJson.post(
      `/${service}/${version}/${endpoint}`,
      data
    );
  },
  getGraphDetail: function <D extends {} | Array<{}>>(
    endpoint: string,
    version: string,
    data: D
  ): AxiosPromise {
    return axiosInstanceGraphExplorer.get(`/${version}/${endpoint}`, {
      params: data,
    });
  },
  getGraphPhoto: function <D extends {} | Array<{}>>(
    endpoint: string,
    version: string,
    data: D
  ): AxiosPromise {
    return axiosInstanceGraphPhoto.get(`/${version}/${endpoint}`, {
      params: data,
    });
  },
  userDirectory: {
    get: function <D extends {} | Array<{}>>(
      service: string,
      endpoint: string,
      data: D,
      version: string | undefined = undefined
    ): AxiosPromise {
      return axiosInstanceForDirectory.get(
        `/${service}/${version}/${endpoint}`,
        {
          params: data,
        }
      );
    },
    getById: function (
      service: string,
      endpoint: string,
      id: number | string
    ): AxiosPromise {
      return axiosInstanceForDirectory.get(`/${service}/v1/${endpoint}/${id}`);
    },
  },
};

export default AzureHttp;
